// Sentry 로 나가는 이벤트·breadcrumb 의 URL 에서 토큰 쿼리·fragment 값을 지운다.
//
// 왜 필요한가: 브라우저 SDK 는 에러 시점의 `location.href` 를 `event.request.url` 에,
// 라우팅·fetch 의 URL 을 breadcrumb(`data.from`/`data.to`/`data.url`)에 **원문 그대로** 싣는다.
// 그런데 이 앱은 토큰을 URL 로 받는 진입점이 여럿이다:
//   - `/auth/success?token=`        — 솔루션 세션 JWT(7일)
//   - `/campus-sso/select?sso_token=` — 캠퍼스 SSO JWT(5분, 세션으로 교환 가능)
//   - `#access_token=`               — OAuth fragment
//   - `/admin/oauth-approve?intent=`  — 외부 앱 관리자 접근 핸들(1회용, 관리자 토큰이 있으면 묶을 수 있다)
// 미로그인으로 튕기면 위 URL 이 `/admin/login?redirect=<인코딩된 URL>` 안에 통째로 실리므로,
// 값 자체가 쿼리를 가진 URL 이면 그 안쪽도 한 번 더 훑는다.
// 백엔드는 `backend/src/utils/sentry_scrub.py` 가 같은 일을 한다. 키 목록은 그쪽과 맞춘다.
//
// 이 파일은 Sentry 타입을 import 하지 않는 순수 함수만 둔다 — node --test 로 바로 돌리기 위해서다.

/** 값을 지울 쿼리·fragment 키 (정확 일치, 소문자 비교) */
const SENSITIVE_KEYS = new Set([
  'token',
  'sso_token',
  'access_token',
  'refresh_token',
  'id_token',
  'code',
  'auth_code',
  'result', // 캠퍼스 link_result·install_result JWT
  'intent', // 외부 앱 OAuth 관리자 접근 핸들 (/admin/oauth-approve)
])

const FILTERED = '[Filtered]'

/** `a=1&token=X` 꼴 문자열에서 민감 키의 값만 바꾼다. 파싱이 필요 없게 정규식으로 처리한다. */
function scrubParams(params: string): string {
  return params.replace(
    /(^|[&;])([^=&;#]+)=([^&;#]*)/g,
    (whole, sep: string, key: string, value: string) => {
      if (SENSITIVE_KEYS.has(decodeKey(key).toLowerCase())) return `${sep}${key}=${FILTERED}`
      // 복귀 경로(`redirect=%2Fadmin%2Foauth-approve%3Fintent%3D…`)처럼 값이 URL 이면 안쪽을 한 번 더 가린다.
      // 바뀐 게 있을 때만 다시 인코딩해, 무관한 값의 원문 표기는 그대로 둔다
      const decoded = decodeKey(value)
      if (decoded.includes('?') || decoded.includes('#')) {
        const inner = scrubUrl(decoded)
        if (inner !== decoded) return `${sep}${key}=${encodeURIComponent(inner)}`
      }
      return whole
    },
  )
}

function decodeKey(key: string): string {
  try {
    return decodeURIComponent(key)
  } catch {
    return key
  }
}

/** URL(절대·상대 모두)의 쿼리와 fragment 에서 토큰 값을 지운다. 문자열이 아니면 그대로 돌려준다. */
export function scrubUrl<T>(url: T): T {
  if (typeof url !== 'string') return url
  const hashAt = url.indexOf('#')
  const beforeHash = hashAt >= 0 ? url.slice(0, hashAt) : url
  const hash = hashAt >= 0 ? url.slice(hashAt + 1) : null
  const qAt = beforeHash.indexOf('?')
  const path = qAt >= 0 ? beforeHash.slice(0, qAt) : beforeHash
  const query = qAt >= 0 ? beforeHash.slice(qAt + 1) : null

  let out = path
  if (query !== null) out += `?${scrubParams(query)}`
  if (hash !== null) out += `#${scrubParams(hash)}`
  return out as T
}

type Dict = Record<string, unknown>

/** beforeSend 용 — request.url·query_string 과 이벤트에 동봉된 breadcrumb 을 훑는다. */
export function scrubSentryEvent<E extends object>(event: E): E {
  const e = event as Dict
  const request = e.request as Dict | undefined
  if (request) {
    request.url = scrubUrl(request.url)
    if (typeof request.query_string === 'string') {
      request.query_string = scrubParams(request.query_string)
    }
    // Referer — 토큰 진입 페이지가 `window.location.href = '/'` 로 전체 로드를 하면 새 문서의
    // document.referrer 가 `/auth/success?token=…` 이 되고, SDK 는 이걸 모든 이벤트의
    // request.headers.Referer 에 싣는다(같은 출처라 전체 URL 이 남는다)
    const headers = request.headers as Dict | undefined
    if (headers && typeof headers === 'object') {
      for (const key of Object.keys(headers)) {
        if (key.toLowerCase() === 'referer') headers[key] = scrubUrl(headers[key])
      }
    }
  }
  const crumbs = e.breadcrumbs
  if (Array.isArray(crumbs)) crumbs.forEach((c) => scrubBreadcrumb(c as object))
  return event
}

/** beforeBreadcrumb 용 — navigation(from/to)·fetch/xhr(url) 의 URL 을 훑는다. */
export function scrubBreadcrumb<B extends object>(crumb: B): B {
  const c = crumb as Dict
  const data = c.data as Dict | undefined
  // 수동 addBreadcrumb 이 원시값 data 를 넘겨도 `in` 에서 던지지 않게 — beforeSend 에서 던지면 이벤트가 통째로 버려진다
  if (data && typeof data === 'object') {
    for (const key of ['url', 'from', 'to']) {
      if (key in data) data[key] = scrubUrl(data[key])
    }
  }
  if (typeof c.message === 'string' && c.message.includes('=')) {
    // 콘솔 breadcrumb 에 URL 이 통째로 찍힌 경우 — URL 모양 토막만 골라 가린다
    c.message = c.message.replace(/\S*[?#]\S*/g, (u) => scrubUrl(u))
  }
  return crumb
}
